import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { authApi, LoginResponse } from '@/api/authApi';

export function useAuth() {
  const navigate = useNavigate();
  const { setAuth, clearAuth, clearStaffSession, clientType, sessionId, setBillingInfo } = useAuthStore();
  const { setTenant, clearTenant } = useTenantStore();

  function applyResponse(response: LoginResponse) {
    const token = response.accessToken ?? response.token ?? '';
    setAuth(token, response.slug, response.sessionId ?? '', response.tenantTimezone);
    setTenant({
      tenantId: response.tenantId,
      slug: response.slug,
      companyName: response.companyName,
      displayName: response.displayName,
      planName: response.planName ?? null,
      storageMode: response.storageMode as 'CloudS3' | 'Local',
      canonicalHost: response.canonicalHost ?? null,
    });
    setBillingInfo(
      response.billingStatus ?? null,
      response.trialEndsOn ?? null,
      response.subscriptionRenewalOn ?? null,
    );
    // Remember workspace for returning-user login (persists through logout)
    try { localStorage.setItem('wl.workspace', response.slug); } catch { /* ignore */ }
  }

  const login = useCallback(
    async (slug: string, onboardingCode: string): Promise<LoginResponse> => {
      const response = await authApi.login(slug, onboardingCode, clientType);
      applyResponse(response);
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientType, setAuth, setTenant, setBillingInfo]
  );

  const googleLogin = useCallback(
    async (idToken: string): Promise<LoginResponse> => {
      const response = await authApi.googleLogin(idToken, clientType);
      applyResponse(response);
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientType, setAuth, setTenant, setBillingInfo]
  );

  const emailPasswordLogin = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const response = await authApi.emailPasswordLogin(email, password, clientType);
      applyResponse(response);
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientType, setAuth, setTenant, setBillingInfo]
  );

  const tenantPasswordLogin = useCallback(
    async (slug: string, email: string, password: string): Promise<LoginResponse> => {
      const response = await authApi.tenantPasswordLogin(slug, email, password, 'Web');
      applyResponse(response);
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAuth, setTenant, setBillingInfo]
  );

  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    await authApi.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<void> => {
    await authApi.resetPassword(token, newPassword);
  }, []);

  const setPassword = useCallback(async (newPassword: string): Promise<void> => {
    await authApi.setPassword(newPassword);
  }, []);

  const logout = useCallback((reason?: 'inactivity' | 'logout') => {
    // Revoke server session before clearing local state (fire-and-forget)
    if (sessionId) authApi.revokeSession(sessionId).catch(() => {});
    clearAuth();
    clearTenant();
    // Intentionally keep wl.workspace so next visit shows "Welcome back"
    navigate('/workspace-login', { state: { reason: reason ?? 'logout' } });
  }, [clearAuth, clearTenant, navigate, sessionId]);

  // Clears only the staff session and returns to the staff picker — tenant session stays.
  // Used for shift handoff: one device, multiple staff members through the day.
  const switchUser = useCallback(() => {
    clearStaffSession();
    navigate('/staff-login');
  }, [clearStaffSession, navigate]);

  return { login, googleLogin, emailPasswordLogin, tenantPasswordLogin, forgotPassword, resetPassword, setPassword, logout, switchUser };
}
